import { useState, useEffect } from 'react';
import { GetPatientsBalanceResponse_PatientBalance } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { TokenPagedQueryHook } from './token-paged-query';

export type UseTokenInfiniteScrollReturn = {
  data: GetPatientsBalanceResponse_PatientBalance[]; // Accumulated data across all pages
  isLoading: boolean; // True on initial load
  isFetchingNextPage: boolean; // True when loading more pages
  hasNextPage: boolean;
  loadMore: () => void;
};

type UseTokenInfiniteScrollParams = {
  enabled: boolean;
  /** Serialized filter state. Any change clears accumulated pages before the next request goes out. */
  filterKey: string;
  useQuery: TokenPagedQueryHook;
};

export const useTokenInfiniteScroll = ({
  enabled,
  filterKey,
  useQuery,
}: UseTokenInfiniteScrollParams): UseTokenInfiniteScrollReturn => {
  const [accumulatedData, setAccumulatedData] = useState<GetPatientsBalanceResponse_PatientBalance[]>([]);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [tokenFilterKey, setTokenFilterKey] = useState(filterKey);

  // Reset accumulated data when filters change. Done during render (not in an effect) so the query
  // never fires with a page token that belongs to the previous filters.
  const isPageTokenStale = tokenFilterKey !== filterKey;
  if (isPageTokenStale) {
    setTokenFilterKey(filterKey);
    setAccumulatedData([]);
    setPageToken(undefined);
    setIsInitialLoad(true);
  }

  const activePageToken = isPageTokenStale ? undefined : pageToken;

  const {
    data: mappedData,
    isFetching,
    nextPageToken,
  } = useQuery({
    enabled,
    pageToken: activePageToken,
    keepPreviousData: true,
  });

  // Accumulate data when new page arrives
  useEffect(() => {
    if (!enabled || isFetching) return;

    if (isInitialLoad) {
      // First page: set accumulated data (may be empty if API returned [])
      setAccumulatedData(mappedData);
      setIsInitialLoad(false);
    } else if (activePageToken && mappedData.length > 0) {
      // Subsequent pages: append to accumulated data, deduping by patientId to guard against refetch duplicates
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((p) => p.patientId));
        return [...prev, ...mappedData.filter((p) => !existingIds.has(p.patientId))];
      });
    }
  }, [enabled, isFetching, mappedData, activePageToken, isInitialLoad]);

  const loadMore = () => {
    if (!isFetching && nextPageToken) {
      setPageToken(nextPageToken);
    }
  };

  return {
    data: accumulatedData,
    isLoading: enabled && isInitialLoad,
    isFetchingNextPage: !isInitialLoad && isFetching,
    hasNextPage: !!nextPageToken,
    loadMore,
  };
};
